<template>
  <TitlesCarousel
    :titles="loop ? titles.expand(22) : titles"
    :navigation-id="navigationId"
    :loop="loop"
    :wrapper-class="swiperWrapperClass"
    :breakpoints="breakpoints"
    v-slot="{ title, index }"
    ref="carousel"
    v-bind="$attrs"
  >
    <TitleDisplayCoverTitle
      :title="title"
      :hide-title="currentlyOverlayingIndex === index"
      :ref="
        (el) =>
          register(index, el as HTMLElement | ComponentPublicInstance | null)
      "
    />
  </TitlesCarousel>
</template>

<script setup lang="ts">
import TitlesCarousel from "./TitlesCarousel.vue";
import TitlesDIsplayCoverOverlay from "./display/TitlesDIsplayCoverOverlay.vue";
import { unrefElement } from "@vueuse/core";
import type { ComponentPublicInstance } from "vue";
import type { TitleEntity } from "~/src/api";

defineComponent({
  inheritAttrs: false,
});

const props = defineProps<{
  titles: TitleEntity[];
  navigationId?: string;
  loop?: boolean;
  swiperWrapperClass?: string;
  breakpoints?: {
    [key in number]: {
      slidesPerView: number;
      spaceBetween?: number;
      autoHeight?: boolean;
    };
  };
}>();

const carousel = ref<InstanceType<typeof TitlesCarousel>>();
const currentlyOverlayingIndex = ref(-1);

const overlay = useOverlay("titles-expand", {
  show: { duration: 300 },
  hide: { duration: 300 },
});

function register(
  index: number,
  instance: HTMLElement | ComponentPublicInstance | null,
) {
  const el = unrefElement(instance);

  if (!el) {
    return;
  }

  overlay.registerElement(
    el,
    {
      component: markRaw(TitlesDIsplayCoverOverlay),
      props: { title: props.titles[index % props.titles.length] },
    },
    {
      onShow: () => (currentlyOverlayingIndex.value = index),
      onHide: () => (currentlyOverlayingIndex.value = -1),
    },
  );
}

defineExpose<{
  slideWidth: ComputedRef<number>;
  SLIDES_DISTANCE: ComputedRef<number>;
  SLIDE_CUTOFF_RATIO: ComputedRef<number>;
}>({
  slideWidth: computed(() => carousel.value?.slideWidth ?? 0),
  SLIDES_DISTANCE: computed(() => carousel.value?.SLIDES_DISTANCE ?? 0),
  SLIDE_CUTOFF_RATIO: computed(() => carousel.value?.SLIDE_CUTOFF_RATIO ?? 0),
});
</script>
